// `/account/action`, WITH THE SERVER'S WEB_ACCOUNTS_ENABLED ON: WHERE OUR ACCOUNT EMAILS' LINKS LAND (ACC-3a).
//
// The server builds each link with the Admin SDK and our website as its handler (ACCOUNTS_PLAN §2.7), so the query is
// Firebase's own: `mode` and `oobCode`. The seam reads it (`parseActionLink`) and opens it (`openActionLink`); this page
// shows what came back, in approved words only:
//   resetPassword        → D3, "Choose a new password", then D4 once saved; D5 when the link is spent or expired.
//   verifyEmail          → the email is confirmed: "Your email is confirmed." (F-ACC3a-2, approved 2026-10-01), shown on
//                          `/checkout` — where C7 told the owner to come back to, whose gate now lets them through — or,
//                          for a tab that holds no appeal, on its own page here. A link already spent (a mail
//                          scanner opened it first) on an account whose email IS confirmed says the same, not D5: the
//                          seam re-reads the account before it answers (`openActionLink`, WALK-FIX-1).
//   verifyAndChangeEmail → the new address is the account's, confirmed: the same as verifyEmail.
//   recoverEmail         → the old address is back (E5's undo, Firebase's own notice). E5 says "undo it now and then
//                          reset your password", so the page goes on to "Reset your password" (D1) with that address in.
//   anything unreadable  → D5: a link that cannot be read is one that cannot be used.
//
// A LINK IS OPENED ONCE PER PAGE LOAD, HOWEVER OFTEN THIS PAGE MOUNTS (R249). `applyActionCode`
// spends the code, so a second call answers "already used" — and this page mounts more than once in a load. React's
// development double-mount is one; the one that looped is our own refresh. A link that worked calls `session.refresh()`,
// which re-runs the shell's `v2Credits` `get` (`App.tsx`); while that is pending the server's account switch is
// unanswered, so `AccountsRoute` shows its spinner and this page UNMOUNTS, and the answer mounts it afresh. The opening
// used to be held in a `useRef`, which survives the double-mount and nothing else: the fresh mount opened the spent code
// again, a confirmed account answered "confirmed" again (WALK-FIX-1), that refreshed again, and so on without end — "Your
// email is confirmed." and the spinner in turn, in a tab holding no appeal (with one, the page goes to `/checkout` and
// out of the loop). Tyler hit it on staging, 2026-10-13. A spent E4 link has no such re-read, so its second opening
// showed D5 over the address it had just confirmed.
//
// So the opening lives outside the component, and the refresh rides on the opening itself: it happens once, whichever
// mount is alive when the answer comes, and every mount only reads the answer. The record is per sign-in seam
// (`session.auth`), which `main.tsx` makes once per page load, so a reload opens the link afresh, as before. A plain
// module-level map keyed by the link alone was measured: the fake mints `fake-oob-1` in every world, so it carried one
// test's answer into the next, and three tests in `accountScreens.test.tsx` and `walkFix1.test.tsx` went red.
//
// AND THE RECORD IS THE SIGNED-IN PERSON'S, AND HOLDS ONLY LINKS THAT OPENED (R-pending-review-fixes; the independent
// review, 2026-10-13). The seam outlives a sign-out: `signOut()` (`services/auth.ts`) starts a fresh look-around under a
// NEW uid on the SAME object, and a sign-in to another account changes the uid on it too. So the record names the uid
// it was made for, and a different signed-in uid starts a fresh one: nobody is shown another person's opened link. A
// moment with nobody signed in (null) is not a different person and keeps the record: an E4 change can end the
// account's tokens, and the SDK then signs it out (`services/auth.ts`, `refreshQuietly`) while its own link's answer is
// on the page; re-opening that spent link would show D5 over the address it had just confirmed (the R249 E4 case). A refused opening is not kept: a network
// failure, or any refusal, is asked again by the next mount. A refusal never refreshes, so nothing remounts the page on
// its own and the retry cannot loop.

import { useEffect, useId, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import type { ActionLink, ActionLinkResult, AuthRefusal, AuthSession } from "../../services";
import { parseActionLink } from "../../services";
import { Busy } from "../../app/accounts";
import { useSession, type Session } from "../../app/session";
import { heldTemplate, heldText } from "../../components/heldCopy";
import { AccountForm, AuthError, HeroTile, PasswordField, Say, SubmitButton } from "./parts";

const EXPIRED: AuthRefusal = { kind: "linkExpired" };

/** The openings on one sign-in seam: whose they are, and each link's, by `${mode} ${oobCode}`. */
interface Openings {
  /** The uid signed in when the record was made; null when nobody was. */
  readonly uid: string | null;
  readonly links: Map<string, Promise<ActionLinkResult>>;
}

/** Each link's opening on this page load, per sign-in seam. */
const openings = new WeakMap<AuthSession, Openings>();

/** This seam's record for whoever is signed in now: a fresh one when a DIFFERENT uid is signed in than the record's. */
function openingsFor(auth: AuthSession): Openings {
  const uid = auth.uid;
  const kept = openings.get(auth);
  if (kept !== undefined && (uid === null || uid === kept.uid)) return kept;
  const fresh: Openings = { uid, links: new Map() };
  openings.set(auth, fresh);
  return fresh;
}

/** Opens `link` the first time it is asked, and refreshes the session once if it worked; after that, the same answer.
 *  A refusal is answered once and forgotten, so the next mount asks again. */
function openOnce(session: Session, link: ActionLink): Promise<ActionLinkResult> {
  const { links } = openingsFor(session.auth);
  const key = `${link.mode} ${link.oobCode}`;
  const kept = links.get(key);
  if (kept !== undefined) return kept;
  const opening: Promise<ActionLinkResult> = session.auth.openActionLink(link).then((result) => {
    // A reset changes nothing about the sign-in until its new password is saved, so it never refreshes.
    if (result.ok && result.kind !== "choosePassword") session.refresh();
    // A refusal is not kept: the network may answer next time. Only this opening is forgotten, never a later one.
    if (!result.ok && links.get(key) === opening) links.delete(key);
    return result;
  });
  links.set(key, opening);
  return opening;
}

export function AccountAction() {
  const session = useSession();
  const [params] = useSearchParams();
  const [link] = useState<ActionLink | null>(() => parseActionLink(params));
  const [opened, setOpened] = useState<ActionLinkResult | null>(link === null ? { ok: false, refusal: EXPIRED } : null);

  useEffect(() => {
    if (link === null) return;
    let alive = true;
    void openOnce(session, link).then((result) => {
      if (alive) setOpened(result);
    });
    return () => {
      alive = false;
    };
    // The link is read once, on arrival; the session's identity does not re-open it.
  }, [link]);

  if (opened === null) return <Busy />;
  if (!opened.ok) return <LinkRefused refusal={opened.refusal} mode={link?.mode ?? null} />;
  switch (opened.kind) {
    case "emailVerified":
    case "emailChanged":
      // F-ACC3a-2 (approved 2026-10-01): "Your email is confirmed." — on `/checkout`, where C7 told the owner to come
      // back to, when this tab holds an appeal; on its own page when it holds none (`/checkout` would send it to `/`).
      return session.appealId !== null
        ? <Navigate to="/checkout" replace state={{ emailConfirmed: true }} />
        : <EmailConfirmed />;
    case "emailRestored":
      return <Navigate to="/forgot" replace state={{ email: opened.email ?? "" }} />;
    case "choosePassword":
      return <ChoosePassword oobCode={(link as ActionLink).oobCode} email={opened.email} />;
  }
}

/** The email-confirmed page (2026-10-14, `CONFIRMED_PAGE_APPROVED`) for a tab that holds no appeal to go back to: a
 *  finished card with one way on. The address shows only when the session knows it; without it, F-ACC3a-2's sentence.
 *  THE STATUS IS THE SENTENCE, NOT THE CARD (the 2026-10-14 review's P3-2): a live region announces its whole content, so
 *  `role="status"` on the card read the heading, the button and the other-tab hint as one announcement. */
function EmailConfirmed() {
  const session = useSession();
  const email = session.auth.email;
  return (
    <section className="account-page" data-account-screen="email-confirmed">
      <div className="confirmed-card">
        <span className="confirmed-tick" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="26" height="26" focusable="false">
            <path d="M5 12.5l4.5 4.5L19 7.5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
        <h1 data-held-copy="account.confirmed.heading">{heldText("account.confirmed.heading")}</h1>
        {email !== null && email !== ""
          ? <p className="copy" role="status" data-held-copy="account.confirmed.body">{heldTemplate("account.confirmed.body", { email })}</p>
          : <p className="copy" role="status" data-held-copy="account.confirm.done">{heldText("account.confirm.done")}</p>}
        <Link className="btn amber-outline block" to="/" data-held-copy="account.confirmed.next">
          {heldText("account.confirmed.next")}
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
            <path d="M5 12h13M13 6.5l5.5 5.5-5.5 5.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </Link>
        <p className="confirmed-hint" data-held-copy="account.confirmed.otherTab">{heldText("account.confirmed.otherTab")}</p>
      </div>
    </section>
  );
}

/** D5 (or another refusal's sentence), and for a reset or undo link, the way to ask for a new one (D1's page). */
function LinkRefused({ refusal, mode }: { refusal: AuthRefusal; mode: ActionLink["mode"] | null }) {
  const askAgain = mode === null || mode === "resetPassword" || mode === "recoverEmail";
  return (
    <section className="account-page" data-account-screen="action-refused">
      <HeroTile />
      <AuthError refusal={refusal} />
      {askAgain && (
        <p className="account-links">
          <Link className="btn ghost block" to="/forgot" data-held-copy="account.forgot.heading">
            {heldText("account.forgot.heading")}
          </Link>
        </p>
      )}
    </section>
  );
}

/** D3, then D4. The address rides along, hidden, so a password manager files the new password under the account. */
function ChoosePassword({ oobCode, email }: { oobCode: string; email: string }) {
  const session = useSession();
  const [password, setPassword] = useState("");
  const [refusal, setRefusal] = useState<AuthRefusal | null>(null);
  const [working, setWorking] = useState(false);
  const [done, setDone] = useState(false);
  const headingId = useId();

  const save = async () => {
    if (working) return;
    setRefusal(null);
    if (password.length < 8) return setRefusal({ kind: "passwordTooShort" });
    setWorking(true);
    try {
      const result = await session.auth.resetPassword(oobCode, password);
      if (!result.ok) return setRefusal(result.refusal);
      setDone(true);
    } finally {
      setWorking(false);
    }
  };

  if (done) {
    return (
      <section className="account-page" data-account-screen="reset-done">
        <HeroTile />
        <div className="note good" role="status">
          <Say id="account.reset.done" className="copy" />
        </div>
        <p className="account-links">
          <Link className="btn primary block" to="/signin" state={{ email }} data-held-copy="account.signIn.button">
            {heldText("account.signIn.button")}
          </Link>
        </p>
      </section>
    );
  }

  return (
    <section className="account-page" aria-labelledby={headingId} data-account-screen="reset">
      <HeroTile />
      <h1 id={headingId} data-held-copy="account.reset.heading">{heldText("account.reset.heading")}</h1>
      <AccountForm onSubmit={() => void save()}>
        <input type="email" name="email" autoComplete="username" value={email} readOnly hidden />
        <PasswordField
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          label="account.reset.field"
          hint
          invalid={refusal?.kind === "passwordTooShort"}
        />
        <AuthError refusal={refusal} />
        <SubmitButton id="account.reset.button" busy={working} />
      </AccountForm>
    </section>
  );
}
